import { useEffect, useState } from "react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Lock, ShieldAlert, Wrench } from "lucide-react";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { TypeUser } from "@/types/userType";
import { PERMISSIONS } from "@/constants/permissions";
import { getBalanceAdjustmentContests } from "@/Api/balanceAdjustments";
import { getSelectUnities } from "@/Api/unities";
import { SellersTab } from "./components/SellersTab";
import { UnitiesTab } from "./components/UnitiesTab";
import { ContestProductsTab } from "./components/ContestProductsTab";
import { LeftoverProductsTab } from "./components/LeftoverProductsTab";
import { AuditsTab } from "./components/AuditsTab";
import { DuplicatesTab } from "./components/DuplicatesTab";
import { BatchCorrectionTab } from "./components/BatchCorrectionTab";
import { ContestOption, UnityOption } from "./types";

/**
 * Ferramenta emergencial de correção manual de balanços.
 *
 * Acesso liberado para:
 * - Super Admin (user_type_id = 1): escopo global, todos os tenants;
 * - Admin do Tenant (user_type_id = 2) com a permissão
 *   "manage-balance-adjustments": escopo limitado ao próprio tenant.
 *
 * A verificação abaixo é apenas de interface. O backend bloqueia os endpoints
 * pelo middleware "panel.permission" e limita o escopo pelo tenant do usuário
 * autenticado.
 */
export const PageList = () => {
  const { user } = useAuth();
  const { user: currentUser, hasPermission } = usePermissions();

  const isSuperAdmin = user?.user_type_id === TypeUser.admin;
  const isTenantUser = user?.user_type_id === TypeUser.client;

  const isTenantAdmin =
    isTenantUser && hasPermission(PERMISSIONS.MANAGE_BALANCE_ADJUSTMENTS);

  /** Permissões chegam pelo /me; enquanto não chegam não é possível decidir. */
  const isCheckingPermissions = !isSuperAdmin && isTenantUser && !currentUser;

  const canAccess = isSuperAdmin || isTenantAdmin;

  const [contests, setContests] = useState<ContestOption[]>([]);
  const [unities, setUnities] = useState<UnityOption[]>([]);
  const [activeTab, setActiveTab] = useState("sellers");

  useEffect(() => {
    if (!canAccess) return;

    const loadReferenceData = async () => {
      const [contestsResponse, unitiesResponse] = await Promise.all([
        getBalanceAdjustmentContests(),
        getSelectUnities(),
      ]);

      if (contestsResponse.ok) setContests(contestsResponse.data?.response ?? []);
      if (unitiesResponse.ok) setUnities(unitiesResponse.data?.response ?? []);
    };

    loadReferenceData();
  }, [canAccess]);

  if (isCheckingPermissions) {
    return (
      <Main>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Main>
    );
  }

  if (!canAccess) {
    return (
      <Main>
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <Lock className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Acesso restrito</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            Esta ferramenta de correção de balanços está disponível para o Super
            Admin e para administradores com a permissão de correção de balanços.
          </p>
        </div>
      </Main>
    );
  }

  return (
    <Main>
      <PageHeader
        icon={Wrench}
        title="Correção de Balanços"
        description="Ferramenta administrativa emergencial para consulta e correção manual de inconsistências"
      />

      <div className="mb-4 flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
        <span>
          Uso restrito. As alterações gravam diretamente os campos de balanço e
          valor preso (quantidades de cotas), sem recálculo, e ficam registradas na
          aba Auditoria.
          {!isSuperAdmin && " Você visualiza e corrige apenas os balanços da sua empresa."}
        </span>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 rounded-lg lg:grid-cols-7 bg-transparent">
          <TabsTrigger value="sellers">Vendedores</TabsTrigger>
          <TabsTrigger value="unities">Unidades</TabsTrigger>
          <TabsTrigger value="contest-products">Concurso/Bolão</TabsTrigger>
          <TabsTrigger value="leftover-products">Sobras</TabsTrigger>
          <TabsTrigger value="batch">Correção em lote</TabsTrigger>
          <TabsTrigger value="duplicates">Duplicidades</TabsTrigger>
          <TabsTrigger value="audits">Auditoria</TabsTrigger>
        </TabsList>

        <TabsContent value="sellers" className="pt-4">
          <SellersTab contests={contests} unities={unities} />
        </TabsContent>

        <TabsContent value="unities" className="pt-4">
          <UnitiesTab contests={contests} unities={unities} />
        </TabsContent>

        <TabsContent value="contest-products" className="pt-4">
          <ContestProductsTab contests={contests} />
        </TabsContent>

        <TabsContent value="leftover-products" className="pt-4">
          <LeftoverProductsTab contests={contests} unities={unities} />
        </TabsContent>

        <TabsContent value="batch" className="pt-4">
          <BatchCorrectionTab contests={contests} />
        </TabsContent>

        <TabsContent value="duplicates" className="pt-4">
          <DuplicatesTab contests={contests} />
        </TabsContent>

        <TabsContent value="audits" className="pt-4">
          <AuditsTab contests={contests} unities={unities} />
        </TabsContent>
      </Tabs>
    </Main>
  );
};

export default PageList;
